import React, { useContext, useEffect, useState } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import UserContext from '../../contexts/UserContext';
import authenticate from '../../utils/Authenticate';
import displayError from '../../utils/ErrorHandler';
import Toast from './Toast';

// SuperAdmin-only screen to edit the single Zabihat catalog row (name / unit /
// price), instead of one-off manual MongoDB edits. Zabihat is a single item
// (unlike SilatItems, which is a list), so this is a one-row form rather than
// the grid used in ManageSilatItems.jsx.
const ManageZabihat = () => {
  const { user, setUser } = useContext(UserContext);
  const navigate = useNavigate();

  const [zabihat, setZabihat] = useState(null); // as fetched
  const [form, setForm] = useState({ name: '', unit: '', price: '' });
  const [fieldErrors, setFieldErrors] = useState({});
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState('');
  const [toast, setToast] = useState({ message: '', type: 'success' });

  useEffect(() => {
    async function init() {
      if (!user) {
        const u = await authenticate(setUser, navigate);
        if (!u) return;
        if (u.role !== 'SuperAdmin') { navigate('/'); return; }
      } else if (user.role !== 'SuperAdmin') {
        navigate('/');
        return;
      }
      fetchZabihat();
    }
    init();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const fetchZabihat = async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${process.env.REACT_APP_API_URI}/api/silatZabihat/getAll`, { withCredentials: true });
      const data = res.data?.data || [];
      const item = data[0] || null;
      setZabihat(item);
      setForm({
        name: item?.name ?? 'Zabihat',
        unit: item?.unit ?? '',
        price: item?.price ?? '',
      });
      setFieldErrors({});
    } catch (err) {
      const msg = displayError(err, navigate, setUser);
      if (msg) setMessage(msg);
    } finally {
      setLoading(false);
    }
  };

  const validateField = (field, value) => {
    if (field === 'name') return !String(value ?? '').trim() ? 'Required' : '';
    if (field === 'unit') return !String(value ?? '').trim() ? 'Required' : '';
    if (field === 'price') {
      return value === '' || value == null || isNaN(value) || Number(value) < 0 ? 'Invalid' : '';
    }
    return '';
  };

  const handleChange = (field, value) => {
    setForm((prev) => ({ ...prev, [field]: value }));
    setFieldErrors((prev) => ({ ...prev, [field]: validateField(field, value) }));
  };

  const isDirty =
    zabihat != null &&
    (form.name !== (zabihat.name ?? '') ||
      form.unit !== (zabihat.unit ?? '') ||
      Number(form.price) !== Number(zabihat.price ?? ''));

  const hasErrors = Object.values(fieldErrors).some(Boolean);

  const handleSave = async () => {
    const errors = {
      name: validateField('name', form.name),
      unit: validateField('unit', form.unit),
      price: validateField('price', form.price),
    };
    setFieldErrors(errors);
    if (Object.values(errors).some(Boolean)) {
      setMessage('Fix the highlighted fields before saving.');
      return;
    }

    setSaving(true);
    setMessage('');
    try {
      await axios.put(
        `${process.env.REACT_APP_API_URI}/api/silatZabihat/update`,
        { name: form.name.trim(), unit: form.unit.trim(), price: Number(form.price) },
        { withCredentials: true }
      );
      setToast({ message: 'Zabihat updated successfully!', type: 'success' });
      await fetchZabihat();
    } catch (err) {
      const msg = displayError(err, navigate, setUser);
      setToast({ message: msg || 'Failed to save changes.', type: 'error' });
    } finally {
      setSaving(false);
    }
  };

  const inputClass = (field) => {
    const err = fieldErrors[field];
    return `w-full p-2 border rounded text-sm ${err ? 'border-red-500 bg-red-50' : 'border-gray-300'}`;
  };

  return (
    <div className="p-4 pb-28 max-w-lg">
      <div className="mb-4">
        <h1 className="text-2xl font-bold text-fmb">Manage Zabihat</h1>
        <p className="text-sm text-gray-600">
          Edit the name, unit, and price for Zabihat — changes apply to every Umaal's commit screen and dashboard.
        </p>
      </div>

      {message && (
        <div className="text-red-600 text-sm bg-red-50 border border-red-200 rounded p-2 mb-3">{message}</div>
      )}

      {loading ? (
        <div className="flex justify-center items-center h-[30vh]">
          <p>Loading...</p>
        </div>
      ) : (
        <div className="bg-white rounded-lg shadow-md p-4 space-y-4">
          <div>
            <label className="block text-xs font-medium text-gray-500 uppercase tracking-wider mb-1">Name</label>
            <input
              type="text"
              value={form.name}
              onChange={(e) => handleChange('name', e.target.value)}
              className={inputClass('name')}
            />
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-500 uppercase tracking-wider mb-1">Unit</label>
            <input
              type="text"
              value={form.unit}
              onChange={(e) => handleChange('unit', e.target.value)}
              className={inputClass('unit')}
            />
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-500 uppercase tracking-wider mb-1">Price (₹)</label>
            <input
              type="number"
              min="0"
              step="0.01"
              value={form.price}
              onChange={(e) => handleChange('price', e.target.value)}
              onWheel={(e) => e.target.blur()}
              className={inputClass('price')}
            />
          </div>
        </div>
      )}

      {isDirty && (
        <div className="fixed bottom-0 left-0 right-0 p-4 bg-yellow-100 shadow-lg flex justify-between items-center z-40">
          <span className="text-sm font-semibold">
            Unsaved changes
            {hasErrors && <span className="text-red-600 ml-2">Fix errors before saving</span>}
          </span>
          <div className="flex gap-2">
            <button
              onClick={() => {
                setForm({ name: zabihat?.name ?? 'Zabihat', unit: zabihat?.unit ?? '', price: zabihat?.price ?? '' });
                setFieldErrors({});
              }}
              className="px-4 py-2 rounded text-sm font-medium bg-gray-200 text-gray-700 hover:bg-gray-300"
            >
              Discard
            </button>
            <button
              onClick={handleSave}
              disabled={saving || hasErrors}
              className={`px-4 py-2 rounded text-white text-sm font-medium ${saving || hasErrors ? 'bg-gray-400' : 'bg-fmb hover:bg-fmb-dark'}`}
            >
              {saving ? 'Saving...' : 'Save Changes'}
            </button>
          </div>
        </div>
      )}

      {toast.message && (
        <Toast
          message={toast.message}
          type={toast.type}
          onClose={() => setToast({ message: '', type: 'success' })}
        />
      )}
    </div>
  );
};

export default ManageZabihat;
